import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, CalendarDays } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TablesInsert } from "@/integrations/supabase/types";
import { generateSlug } from "@/lib/slugUtils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface CreateEventFormData {
  title: string;
  description: string;
  date: Date;
  location: string;
  price_general_admission: number;
  price_vip: number;
  capacity: number;
  category: string;
  image: FileList;
}

export const CreateEventForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<CreateEventFormData>();

  const onSubmit = async (data: CreateEventFormData) => {
    try {
      setIsSubmitting(true);
      console.log("Submitting event data:", data);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please login to create an event");
        navigate("/auth");
        return;
      }

      let image_url = null;
      if (data.image?.[0]) {
        const file = data.image[0];
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('event-images')
          .upload(fileName, file);

        if (uploadError) {
          console.error("Error uploading image:", uploadError);
          toast.error("Failed to upload image");
          return;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('event-images')
          .getPublicUrl(fileName);
        
        image_url = publicUrl;
      }

      // Generate a clean SEO-friendly slug from the title
      const seoUrl = generateSlug(data.title);
      console.log("Generated SEO URL:", seoUrl);

      const eventData: TablesInsert<"events"> = {
        producer_id: session.user.id,
        title: data.title,
        description: data.description,
        date: data.date.toISOString(),
        location: data.location,
        price_general_admission: data.price_general_admission,
        price_vip: data.price_vip,
        capacity: data.capacity,
        category: data.category,
        image_url,
        template_type: 'no_seating',
        seo_url: seoUrl,
        status: 'pending_review'
      };

      const { error } = await supabase.from("events").insert(eventData);

      if (error) {
        console.error("Error submitting event:", error);
        toast.error("Failed to submit event");
        return;
      }

      toast.success("Event submitted for review! We'll notify you once it's approved.");
      navigate("/");
    } catch (error) {
      console.error("Error in form submission:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form 
        onSubmit={form.handleSubmit(onSubmit)} 
        className="space-y-6 bg-black/50 rounded-lg p-8 backdrop-blur-sm border border-gray-800 shadow-xl"
      >
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white font-medium">Event Title</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter event title" 
                  {...field} 
                  className="text-white placeholder:text-gray-400 bg-black/30 border-gray-700 focus:border-primary hover:border-gray-600 transition-colors"
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white font-medium">Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe your event"
                  className="resize-none text-white placeholder:text-gray-400 bg-black/30 border-gray-700 focus:border-primary hover:border-gray-600 transition-colors min-h-[120px]"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="date"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel className="text-white font-medium">Date</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={`w-full pl-3 text-left font-normal ${
                        !field.value && "text-muted-foreground"
                      }`}
                    >
                      {field.value ? (
                        format(field.value, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarDays className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    disabled={(date) =>
                      date < new Date() || date < new Date("1900-01-01")
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white font-medium">Location</FormLabel>
              <FormControl>
                <Input placeholder="Event location" {...field} className="bg-black/30 border-gray-700 text-white placeholder:text-gray-400" />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="price_general_admission"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white font-medium">General Admission Price</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  {...field}
                  className="bg-black/30 border-gray-700 text-white placeholder:text-gray-400"
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="price_vip"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white font-medium">VIP Price</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  {...field}
                  className="bg-black/30 border-gray-700 text-white placeholder:text-gray-400"
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="capacity"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white font-medium">Capacity</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min="1"
                  placeholder="Enter event capacity"
                  {...field}
                  className="bg-black/30 border-gray-700 text-white placeholder:text-gray-400"
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white font-medium">Category</FormLabel>
              <FormControl>
                <Input placeholder="Event category" {...field} className="bg-black/30 border-gray-700 text-white placeholder:text-gray-400" />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="image"
          render={({ field: { value, onChange, ...field } }) => (
            <FormItem>
              <FormLabel className="text-white font-medium">Event Image</FormLabel>
              <FormControl>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onChange(e.target.files)}
                  {...field}
                  className="bg-black/30 border-gray-700 text-white placeholder:text-gray-400"
                />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-medium py-3 transition-all duration-200 ease-in-out transform hover:scale-[1.02]"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting Event...
            </>
          ) : (
            "Submit Event for Review"
          )}
        </Button>
      </form>
    </Form>
  );
};
